'use client';

import { useEffect } from 'react';
import NextImage from 'next/image';
import { PortableText } from '@portabletext/react';
import { richTextComponent } from '@/sanity/sanityPortableText';
import { CTAButton } from '@/shadcn/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shadcn/ui/dialog';
import { IPromoBanner } from '@/sanity/lib/definitions';
import { resolveI18nRichtext } from '@/sanity/globals/I18n';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { usePromoPopupOnce } from '@/hooks/usePromoPopupOnce';

interface PromoBannerDialogClassicProps {
  data: IPromoBanner;
  language?: string;
  /** Preview harnesses: open immediately, ignore the once-a-day stamp. */
  forceOpen?: boolean;
}

export default function PromoBannerDialogClassic({
  data,
  language,
  forceOpen,
}: PromoBannerDialogClassicProps) {
  const [open, setOpen] = usePromoPopupOnce(forceOpen);

  // Same rule as TopBanner: the translation wins on non-default languages and
  // falls back to the stored copy when that language is empty. The button gets
  // the same language: CTAButton alone reads it from the path, which has no
  // /de or /br prefix on affiliate pages.
  const text = resolveI18nRichtext(data.text, data.textI18n, language);

  useEffect(() => {
    if (open) document.body.style.overflow = 'unset';
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="!w-[min(83.75rem,calc(100%-4rem))] p-0 overflow-hidden bg-white rounded-[0.25rem] !duration-700 ease-in">
        <VisuallyHidden>
          <DialogHeader>
            <DialogTitle></DialogTitle>
          </DialogHeader>
        </VisuallyHidden>
        {/* Image section */}
        {data.image && (
          <div className="relative lg:h-[46.5rem] h-[40rem] w-full overflow-hidden bg-[#E6EDEB]">
            <NextImage
              src={data.image.src}
              alt={data.image.name || ''}
              fill
              className="object-cover object-bottom"
              sizes="562px"
              placeholder="blur"
              blurDataURL={data.image.lqip}
            />
          </div>
        )}

        {/* Text + button section */}
        <div className="flex px-[2rem] flex-col items-center text-center gap-[4rem] py-[6rem] lg:py-[8.62rem]">
          {text && (
            <div className="richtext prose-headings:font-[600] prose-headings:lg:text-[5.75rem] flex flex-col gap-[2rem] max-w-[40ch]">
              <PortableText value={text} components={richTextComponent} />
            </div>
          )}
          {data.button && (
            <CTAButton data={data.button} language={language} />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
